<template>
  <div class="data-panel">
    <div v-if="!detail" style="color:#9ca3af;font-size:14px;">Carregando...</div>
    <template v-else>
      <h3 class="data-title">Configurações da Obra</h3>
      <div class="data-settings">
        <div class="data-field">
          <label>Nome da obra</label>
          <input v-model="configSiteName" type="text" placeholder="Nome da obra" />
        </div>
        <div class="data-field">
          <label>Cor primária</label>
          <div style="display:flex;gap:8px;align-items:center;">
            <input v-model="configPrimaryColor" type="color" style="width:40px;height:36px;border:none;cursor:pointer;background:none;" />
            <input v-model="configPrimaryColor" type="text" placeholder="#0f4c81" style="flex:1;" />
          </div>
        </div>
        <div class="data-field">
          <label>Cor secundária</label>
          <div style="display:flex;gap:8px;align-items:center;">
            <input v-model="configSecondaryColor" type="color" style="width:40px;height:36px;border:none;cursor:pointer;background:none;" />
            <input v-model="configSecondaryColor" type="text" placeholder="#e8f0f7" style="flex:1;" />
          </div>
        </div>
        <div class="data-field">
          <label>Logotipo da navbar</label>
          <select v-model="configLogoType" style="width:100%;padding:8px;border:1px solid #ddd;border-radius:4px;">
            <option value="default">ObraTycoon by GusMachado</option>
            <option value="by-gus">by GusMachado</option>
            <option value="custom">Personalizado</option>
          </select>
        </div>
        <div v-if="configLogoType === 'custom'" class="data-field">
          <label>Texto personalizado</label>
          <input v-model="configCustomLogoText" type="text" placeholder="Digite o texto que deseja exibir" />
        </div>
        <p v-if="configError" class="data-error">{{ configError }}</p>
        <p v-if="configSuccess" class="data-success">{{ configSuccess }}</p>
        <button class="btn-admin-primary" type="button" :disabled="configSaving" @click="saveConfig">
          {{ configSaving ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>

      <hr class="data-sep" />

      <h3 class="data-title">Alterar senha de usuário</h3>
      <div class="data-settings">
        <div class="data-field">
          <label>Usuário</label>
          <select v-model="pwdUsername" class="data-select">
            <option value="">Selecione um usuário</option>
            <option v-for="u in detail.users" :key="u.username" :value="u.username">{{ u.name }} ({{ u.username }})</option>
          </select>
        </div>
        <div class="data-field">
          <label>Nova senha</label>
          <input v-model="pwdNew" type="password" placeholder="Nova senha" />
        </div>
        <p v-if="pwdError" class="data-error">{{ pwdError }}</p>
        <p v-if="pwdSuccess" class="data-success">{{ pwdSuccess }}</p>
        <button class="btn-admin-primary" type="button" :disabled="pwdSaving" @click="saveUserPassword">
          {{ pwdSaving ? 'Salvando...' : 'Alterar senha' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { ref, inject, onMounted, watch } from 'vue'
import { masterFetch } from '@/store/masterSession'

export default {
  name: 'AdminSysConfigTab',
  setup() {
    const detail = inject<any>('adminDetail')

    // Config fields
    const configSiteName = ref('')
    const configPrimaryColor = ref('')
    const configSecondaryColor = ref('')
    const configLogoType = ref('default')
    const configCustomLogoText = ref('')
    const configSaving = ref(false)
    const configError = ref('')
    const configSuccess = ref('')

    // Password fields
    const pwdUsername = ref('')
    const pwdNew = ref('')
    const pwdSaving = ref(false)
    const pwdError = ref('')
    const pwdSuccess = ref('')

    const initForm = () => {
      if (detail && detail.value) {
        configSiteName.value = detail.value.siteName || ''
        configPrimaryColor.value = detail.value.primaryColor || '#0f4c81'
        configSecondaryColor.value = detail.value.secondaryColor || '#e8f0f7'
        configLogoType.value = detail.value.logoType || 'default'
        configCustomLogoText.value = detail.value.customLogoText || ''
      }
    }

    onMounted(initForm)
    watch(() => detail?.value, initForm)

    const saveConfig = async () => {
      configError.value = ''
      configSuccess.value = ''
      if (!detail.value) return
      configSaving.value = true
      try {
        await masterFetch(`/master/systems/${detail.value.systemId}/config`, {
          method: 'PUT',
          body: JSON.stringify({
            siteName: configSiteName.value,
            primaryColor: configPrimaryColor.value,
            secondaryColor: configSecondaryColor.value,
            logoType: configLogoType.value,
            customLogoText: configCustomLogoText.value
          })
        })
        if (detail.value) {
          if (configSiteName.value) detail.value.siteName = configSiteName.value
          if (configPrimaryColor.value) detail.value.primaryColor = configPrimaryColor.value
          if (configSecondaryColor.value) detail.value.secondaryColor = configSecondaryColor.value
          if (configLogoType.value) detail.value.logoType = configLogoType.value
          if (configCustomLogoText.value) detail.value.customLogoText = configCustomLogoText.value
        }
        configSuccess.value = 'Configurações salvas.'
      } catch (err: unknown) {
        configError.value = err instanceof Error ? err.message : 'Erro ao salvar.'
      } finally {
        configSaving.value = false
      }
    }

    const saveUserPassword = async () => {
      pwdError.value = ''
      pwdSuccess.value = ''
      if (!pwdUsername.value || !pwdNew.value) {
        pwdError.value = 'Selecione um usuário e informe a nova senha.'
        return
      }
      if (!detail.value) return
      pwdSaving.value = true
      try {
        await masterFetch(`/master/systems/${detail.value.systemId}/user-password`, {
          method: 'PUT',
          body: JSON.stringify({ username: pwdUsername.value, newPassword: pwdNew.value })
        })
        pwdSuccess.value = `Senha de "${pwdUsername.value}" alterada com sucesso.`
        pwdNew.value = ''
        pwdUsername.value = ''
      } catch (err: unknown) {
        pwdError.value = err instanceof Error ? err.message : 'Erro ao alterar senha.'
      } finally {
        pwdSaving.value = false
      }
    }

    return {
      detail,
      configSiteName, configPrimaryColor, configSecondaryColor, configLogoType, configCustomLogoText,
      configSaving, configError, configSuccess, saveConfig,
      pwdUsername, pwdNew, pwdSaving, pwdError, pwdSuccess, saveUserPassword
    }
  }
}
</script>

<style lang="scss">
/* Shared styles — defined in AdminSysDataTab, reused here */
</style>
